package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.Space
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/**
 * The measure a settings *form* is read at.
 *
 * A text field is read by moving the eye from its label to its caret, and a field stretched across
 * a 1128px page turns that into a head movement — which is why every settings form in the product
 * sits inside the page's measure rather than filling it. Named here rather than written as
 * `48.cssRem` at each call site so the project settings tabs and the workspace settings pages
 * cannot answer the question differently: [SettingsSection] reads it, and so do the nine workspace
 * pages that have no section wrapper of their own.
 */
internal val SettingsFormMeasure = 48.cssRem

/**
 * The measure a settings section is read at when it holds a table, a card grid or a chart.
 *
 * Wider than [SettingsFormMeasure] and still narrower than the 1128px surface, because a table
 * that runs the full measure has no visual right edge to end on.
 */
internal val SettingsWideMeasure = 70.5.cssRem

/** The app shell is the sole horizontal-gutter owner. This surface owns measure and rhythm only. */
val ProjectSettingsSurfaceStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .styleModifier { property("box-sizing", "border-box") }
}

@Composable
internal fun ProjectSettingsSurface(content: @Composable () -> Unit) {
    Div(ProjectSettingsSurfaceStyle.toModifier().attr("data-settings-surface", "").toAttrs()) {
        Column(
            Modifier
                .fillMaxWidth()
                .maxWidth(1128.px)
                .styleModifier {
                    property("margin-left", "auto")
                    property("margin-right", "auto")
                }
                .gap(Space.XXXL),
        ) {
            content()
        }
    }
}
